import { useEffect, useState, useRef } from 'react';
import { X, ImagePlus, Star, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Turn a stored image URL into something the browser can render. Relative
// `/api/images/...` paths need to be prefixed with the backend host in dev
// (where frontend and backend are on different origins).
function resolveSrc(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) return `${API_BASE}${url.slice(4)}`; // strip '/api'
  return url;
}

export default function ProductImageManager({ product, open, onOpenChange, onSaved }) {
  const [images, setImages] = useState(product?.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    setImages(product?.images || []);
  }, [product?.id, product?.images]);

  if (!product) return null;

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${product.id}/images`, { images: next });
      setImages(next);
      onSaved?.(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      let latest = images;
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`/admin/products/${product.id}/images`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        latest = data.images;
      }
      setImages(latest);
      onSaved?.(latest);
      toast.success(`Uploaded ${fileList.length} image${fileList.length > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const setPrimary = (idx) => {
    if (idx === 0) return;
    const next = [images[idx], ...images.filter((_, i) => i !== idx)];
    persist(next);
  };
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    persist(next);
  };
  const moveDown = (idx) => {
    if (idx === images.length - 1) return;
    const next = [...images];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    persist(next);
  };
  const remove = (idx) => {
    if (!window.confirm('Remove this image from the product?')) return;
    const next = images.filter((_, i) => i !== idx);
    persist(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="product-image-manager">
        <DialogHeader>
          <DialogTitle className="h-display text-2xl">Images</DialogTitle>
          <p className="sans text-xs uppercase tracking-widest text-neutral-500">
            {product.sku} · {product.name}
          </p>
        </DialogHeader>

        <div
          className="mt-4 border-2 border-dashed border-neutral-200 hover:border-[color:var(--ije-pink)] transition-colors p-6 text-center cursor-pointer bg-neutral-50/60"
          onClick={() => fileInput.current?.click()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
          onDragOver={(e) => e.preventDefault()}
          data-testid="image-upload-dropzone"
        >
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
            multiple
            className="hidden"
            onChange={(e) => upload(e.target.files)}
            data-testid="image-upload-input"
          />
          <div className="flex items-center justify-center gap-2 text-neutral-600">
            {uploading
              ? <Loader2 size={16} className="animate-spin" />
              : <ImagePlus size={18} strokeWidth={1.5} />}
            <span className="sans text-sm">
              {uploading ? 'Uploading…' : 'Click or drop JPG · PNG · WebP up to 10MB'}
            </span>
          </div>
        </div>

        {images.length === 0 ? (
          <p className="sans text-sm text-neutral-500 text-center py-6">
            No images yet — the storefront will show the IJE HAUS placeholder.
          </p>
        ) : (
          <ul className="mt-5 divide-y divide-neutral-100 max-h-96 overflow-y-auto">
            {images.map((url, idx) => (
              <li key={url} className="flex items-center gap-3 py-3" data-testid={`image-row-${idx}`}>
                <div className="w-16 h-16 border border-neutral-200 flex-shrink-0 bg-neutral-50">
                  <img src={resolveSrc(url)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="sans text-xs text-neutral-500 uppercase tracking-widest">
                    {idx === 0 ? 'Primary' : `#${idx + 1}`}
                  </p>
                  <p className="sans text-xs text-neutral-500 truncate">{url}</p>
                </div>
                <div className="flex items-center gap-1">
                  {idx !== 0 && (
                    <button
                      type="button"
                      onClick={() => setPrimary(idx)}
                      className="p-2 hover:bg-neutral-100 rounded-sm"
                      title="Make primary"
                      data-testid={`image-primary-${idx}`}
                    >
                      <Star size={14} strokeWidth={1.5} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="p-2 hover:bg-neutral-100 rounded-sm disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(idx)}
                    disabled={idx === images.length - 1}
                    className="p-2 hover:bg-neutral-100 rounded-sm disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="p-2 text-neutral-500 hover:text-red-600 rounded-sm"
                    title="Remove image"
                    data-testid={`image-remove-${idx}`}
                  >
                    <X size={14} strokeWidth={1.5} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {saving && (
          <p className="sans text-[10px] text-neutral-400 mt-2 flex items-center gap-1">
            <Loader2 size={10} className="animate-spin" /> Saving…
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
